import React from "react";
import {Box} from "@mui/material";
import {popoverMinWidth} from "@/common/overview-table/column-filter-width";

/** One field placed in a `ColumnFilterRow` — only its label and its suffix (unit or currency, wired by the
 *  Phase 3.2 request range filters) feed the width computation, the actual field is rendered by the caller
 *  as a child. */
export interface ColumnFilterRowField {
    readonly label: string;
    readonly suffix?: string;
}

export interface ColumnFilterRowProps {
    /** Labels and suffixes of every field in this row, in the same order as `children` — used only to
     *  compute the row's width via `column-filter-width.ts`, never rendered here. */
    fields: readonly ColumnFilterRowField[];
    /** The actual field elements (e.g. two `DateInputField`s), rendered side by side. */
    children: React.ReactNode;
}

/**
 * Lays out a range filter's "von" and "bis" fields (or any other fixed set of fields) side by side
 * in ONE row (plan "Spaltenkopf-Filterung und Sortieranzeige", Standing constraint 9), sized via
 * `column-filter-width.ts` rather than a guessed constant — see that module's doc comment for the
 * #1609 overflow this guards against. Every child shares the row equally (`flex: 1`); the row's own
 * width is set explicitly rather than left to `min-content`; a MUI text field's floating label can
 * otherwise report a smaller intrinsic width than the label needs once it de-floats.
 */
export const ColumnFilterRow: React.FC<ColumnFilterRowProps> = ({fields, children}) => {
    const longestLabel = fields.reduce((longest, field) => (field.label.length > longest.length ? field.label : longest), "");
    const longestSuffix = fields.reduce(
        (longest, field) => ((field.suffix?.length ?? 0) > longest.length ? (field.suffix ?? "") : longest),
        ""
    );
    const width = popoverMinWidth(fields.length, longestLabel, longestSuffix || undefined);

    return (
        <Box sx={{display: "flex", flexDirection: "row", gap: 2, width}}>
            {React.Children.map(children, (child) => (
                <Box sx={{flex: 1, minWidth: 0}}>{child}</Box>
            ))}
        </Box>
    );
};
